import CollectionPlaceholder from '@design-system/collection-placeholder';
import React, { useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import { useProjectQuery, useWorkInfiniteQuery } from '@hook/gridgio/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import { createProjectPath } from '@constant/gridgio/route-path';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import IconTextBlock from '@component/common/block/icon-text-block';
import IconBlock from '@component/common/block/icon-block';
import TextBlock from '@component/common/block/text-block';
import PlanThumbnail from '@component/gridgio/project/plan/plan-thumbnail';
import MapThumbnail from '@component/common/map-thumbnail';
import Gap from '@component/common/gap';
import SearchInput from '@design-system/input/search-input';
import Skeleton from '@design-system/skeleton';
import Canvas from '@design-system/canvas';
import TextButton from '@design-system/text-button';
import Chip from '@design-system/chip';
import Line from '@component/common/line';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';

const MapThumbnailContainer = styled.div`
  width: 250px;
  height: 150px;
  overflow: hidden;
  border-radius: ${size.BORDER_RADIUS_1};
`;

const PlanThumbnailContainer = styled.div`
  width: 120px;
  height: 100px;
  overflow: hidden;
  border-radius: ${size.BORDER_RADIUS_1};
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
`;

const ProjectHeaderContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${size.MARGIN_2};
  padding-bottom: ${size.MARGIN_2};
  border-bottom: ${size.BORDER_WIDTH_1} solid ${color.GRAY_2};
`;

const ProjectConfigContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  flex-shrink: 0;
  align-items: center;
  padding: 0 ${size.MARGIN_1};
  background-color: ${color.WHITE_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.BLACK_5};
  border-radius: ${size.BORDER_RADIUS_2};
`;

const ProjectInfoContainer = styled.div`
  display: flex;
  min-width: 0;
  flex: 1 1 250px;
  flex-direction: column;
  align-items: flex-start;
`;

const ProjectMetaContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const ProjectTextContainer = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
`;
const WorkControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${size.MARGIN_2};
`;
const SearchContainer = styled.div`
  flex: 1;
  min-width: 0;
`;
const WorkListControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const WorkItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size.MARGIN_1};
`;

const WorkItemContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: ${size.MARGIN_2};
`;

const WorkInfoContainer = styled.div`
  display: flex;
  align-self: stretch;
  justify-content: space-between;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
`;

const WorkTitleDescriptionContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const WorkPlanDateContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const WorkActionControlContainer = styled.div`
  display: flex;
  align-self: stretch;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
`;

const WorkActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const PROJECT_CONFIG_TEXT_DESIGN = {
  box: { height: '35px', gap: '5px', event: { cursor: 'pointer' } },
  icon: { size: 'small', color: color.BLACK_5 },
  text: { size: 'medium', color: color.BLACK_5 },
} as const;

const Item: React.FC = () => {
  const dispatch = useTypedDispatch();
  const handleMapPreview = (latitude?: number, longitude?: number) => {
    if (latitude === undefined || longitude === undefined) return;
    dispatch(modalActions.showDialogModal({ type: 'map-preview', data: { latitude, longitude } }));
  };
  const navigate = useNavigate();
  const { projectKey = '' } = useParams();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const projectQuery = useProjectQuery(projectKey);
  const workQuery = useWorkInfiniteQuery(projectKey);
  const project = projectQuery.data?.data;
  const works = useMemo(() => workQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [], [workQuery.data]);
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: Boolean(project) && (workQuery.hasNextPage ?? false) && !workQuery.isFetchNextPageError,
    isFetchingNextPage: workQuery.isFetchingNextPage,
    fetchNextPage: workQuery.fetchNextPage,
    root: scrollContainerRef,
  });
  // TODO: 작업 검색·정렬 API를 연결한다.
  const handlePlannedClick = () => dispatch(modalActions.showDialogModal({ type: 'feature-planned' }));
  const handleStartWork = () => dispatch(modalActions.showDialogModal({ type: 'start-work', data: { projectKey } }));
  const WorkItems = works.map((work) => (
    <Canvas
      key={work.key}
      theme={ETheme.WHITE}
      width='100%'
      padding={size.MARGIN_1}
      border
      shadow>
      <WorkItemContainer>
        {work.plan && (
          <PlanThumbnailContainer>
            <PlanThumbnail source={work.plan.source} />
          </PlanThumbnailContainer>
        )}
        <WorkInfoContainer>
          <WorkTitleDescriptionContainer>
            <TextBlock
              text={work.name}
              design={{
                box: { width: '100%', alignX: 'left' },
                text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
              }}
            />
            {work.description && (
              <TextBlock
                text={work.description}
                design={{
                  box: { width: '100%', alignX: 'left' },
                  text: { size: 'medium', color: color.BLACK_4, oneline: true },
                }}
              />
            )}
          </WorkTitleDescriptionContainer>
          <WorkPlanDateContainer>
            <IconTextBlock
              icon='files'
              text={work.plan?.name ?? '도면 미등록'}
              design={{
                box: { width: '100%', alignX: 'left', gap: '2px' },
                icon: { size: 'tiny', color: color.BLACK_5 },
                text: { size: 'small', color: color.BLACK_5, oneline: true },
              }}
            />
            <IconTextBlock
              icon='clock'
              text={new Date(work.updatedAt).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' })}
              design={{
                box: { gap: '2px' },
                icon: { size: 'tiny', color: color.GRAY_1 },
                text: { size: 'small', color: color.GRAY_1 },
              }}
            />
          </WorkPlanDateContainer>
        </WorkInfoContainer>
        <WorkActionControlContainer>
          <WorkActionContainer>
            <IconBlock
              icon='edit'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{
                handler: () => dispatch(modalActions.showDialogModal({
                  type: 'work-edit',
                  data: { workKey: work.key, name: work.name, description: work.description },
                })),
              }}
            />
            <IconBlock
              icon='trash'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{
                handler: () => dispatch(modalActions.showDialogModal({
                  type: 'work-remove',
                  data: { projectKey, workKey: work.key },
                })),
              }}
            />
          </WorkActionContainer>
          <TextButton
            text='작업 열기'
            theme={ETheme.CYAN}
            tone='normal'
            kind='outlined'
            size='small'
            shape='round'
            width='100%'
            // height='32px'
            handleClick={() => navigate(createProjectPath('work', work.key))}
          />
        </WorkActionControlContainer>
      </WorkItemContainer>
    </Canvas>
  ));

  const isWorkLoading = workQuery.isLoading;
  if (projectQuery.isError && !project) return null;

  return (
    <Wrapper>
      <ProjectHeaderContainer>
        <ProjectInfoContainer>
          {project ? (
            <>
              <TextBlock
                text={project.name}
                design={{
                  box: { width: '100%', alignX: 'left' },
                  text: { size: 'big', color: color.BLACK_2, bold: true, oneline: true },
                }}
              />
              <Gap height={size.MARGIN_1} />
              <ProjectMetaContainer>
                <MapThumbnailContainer>
                  <MapThumbnail
                    latitude={project.coordinate?.latitude}
                    longitude={project.coordinate?.longitude}
                    handleExpandClick={() => handleMapPreview(project.coordinate?.latitude, project.coordinate?.longitude)}
                  />
                </MapThumbnailContainer>
                <Gap width={size.MARGIN_1} />
                <ProjectTextContainer>
                  <IconTextBlock
                    icon='text-align-justify'
                    text={project.description?.trim() || '설명 미등록'}
                    design={{
                      box: { width: '100%', gap: '5px', alignX: 'left' },
                      icon: { size: 'small', color: color.BLACK_4 },
                      text: { size: 'medium', color: color.BLACK_4 },
                    }}
                  />
                  <Gap height={size.MARGIN_1} />
                  <IconTextBlock
                    icon='map-pin'
                    text={[project.place, project.address].filter(Boolean).join(' · ') || '주소 미등록'}
                    design={{
                      box: { gap: '5px', alignX: 'left' },
                      icon: { size: 'small', color: color.BLACK_4 },
                      text: { size: 'medium', color: color.BLACK_4 },
                    }}
                  />
                </ProjectTextContainer>
              </ProjectMetaContainer>
            </>
          ) : (
            <>
              <Skeleton tone='normal' width='180px' height='35px' />
              <Gap height={size.MARGIN_1} />
              <ProjectMetaContainer>
                <Skeleton tone='normal' width='250px' height='150px' />
                <Gap width={size.MARGIN_1} />
                <ProjectTextContainer>
                  <Skeleton tone='normal' width='180px' height='25px' />
                  <Gap height={size.MARGIN_1} />
                  <Skeleton tone='normal' width='180px' height='25px' />
                </ProjectTextContainer>
              </ProjectMetaContainer>
            </>
          )}
        </ProjectInfoContainer>
        <ProjectConfigContainer>
          <IconTextBlock
            text='도면 관리'
            icon='files'
            design={PROJECT_CONFIG_TEXT_DESIGN}
            click={{ handler: () => navigate(createProjectPath('plan', projectKey)) }}
          />
          <Line direction='vertical' height='15px' color={color.GRAY_2} margin={`0px 10px 0px 10px`} />
          <IconTextBlock
            text='설정'
            icon='setting'
            design={PROJECT_CONFIG_TEXT_DESIGN}
            click={{
              handler: () => {
                if (project) {
                  dispatch(modalActions.showDialogModal({ type: 'project-edit', data: project }));
                }
              },
            }}
          />
        </ProjectConfigContainer>
      </ProjectHeaderContainer>
      <Gap height={size.MARGIN_2} />
      <WorkControlContainer>
        <SearchContainer>
          <SearchInput
            value=''
            placeholder='작업명 또는 도면명으로 검색'
            theme={ETheme.CYAN}
            width='100%'
            readOnly
            handleClick={handlePlannedClick}
            handleSearch={handlePlannedClick}
          />
        </SearchContainer>
        <TextButton
          text='새 작업'
          icon='clipboard-plus'
          arrangement='icon-text'
          theme={ETheme.CYAN}
          size='medium'
          width='100px'
          shape='pill'
          handleClick={handleStartWork}
        />
      </WorkControlContainer>
      <Gap height={size.MARGIN_2} />
      <WorkListControlContainer>
        <Chip
          isLoading={isWorkLoading}
          text={`전체 작업 ${workQuery.data?.pages[0]?.totalCount ?? works.length}개`}
          theme={ETheme.CYAN}
          kind='outlined'
          size='small'
          shape='round'
          usage='static'
        />
        <IconBlock
          icon='sort'
          design={{
            box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
            icon: { size: 'small', color: color.CYAN_2 },
          }}
          click={{ handler: handlePlannedClick }}
        />
      </WorkListControlContainer>
      <Gap height={size.MARGIN_1} />
      <ScrollContainer width='100%'>
        <Scroll direction='y' height='100%' containerRef={scrollContainerRef}>
          {isWorkLoading ? (
            <WorkItemsContainer>
              <Skeleton tone='normal' height='90px' />
              <Skeleton tone='normal' height='90px' />
              <Skeleton tone='normal' height='90px' />
            </WorkItemsContainer>
          ) : works.length > 0 ? (
            <>
              <WorkItemsContainer>{WorkItems}</WorkItemsContainer>
              <div ref={sentinelRef} style={{ width: '100%', height: '1px', flexShrink: 0 }} />
              {workQuery.isFetchingNextPage && <Skeleton tone='normal' height='90px' />}
            </>
          ) : (
            <CollectionPlaceholder
              theme={ETheme.CYAN}
              width='100%'
              height='100%'
              icon='archive-x'
              title='첫 작업을 시작해보세요'
              description='도면을 선택하고 현장의 상태를 기록하세요.'
              buttonText='작업 시작하기'
              handleButtonClick={handleStartWork}
            />
          )}
        </Scroll>
      </ScrollContainer>
    </Wrapper>
  );
};
export default Item;
